import i18n from "@/i18n";
import cardState from "../../filters/cardState.filter";
import { ActionModel } from "../../models/ActionModel";
import { DeclarationPropertiesTypes } from "fs-pb-vue2/data/enums/declarationPropertiesTypes";
import {OperationsApi} from "fs-pb-vue2/services/api/OperationsApi";
import { OperationRequestData } from "fs-pb-vue2/data/operationRequestData";
import { CardModel } from "../../models/contracts/cardModel";
import { Prop, Watch } from "vue-property-decorator";
import { container } from "@/inversify.config";
import { BaseContractVue } from "./BaseContractVue";
import IAlertService from "fs-pb-vue2/services/alert/IAlertService";

/**
 *  Класс для компоненты карты.
 */
export class CardVue extends BaseContractVue {

  /**Идентификатор карты */
  @Prop() cardId!: number;

  /** отслеживаем идентификатор карты */
  @Watch("cardId", { immediate: true })
  cardIdChanged() {
    this.init();
    this.contractsStore.setSelected(this.cardId.toString());
    this.isAliasEdit = false;
  }

  /** отслеживаем имя карты */
  @Watch("getAlias", { immediate: true })
  aliasChange(newVal: string) {
    this.alias = newVal.substring(0, 20);
  }

  /** отслеживаем объект карты */
  @Watch("card", { immediate: true })
  cardChanged(newVal: CardModel) {
    if (newVal) {
      this.isVisible = !newVal.Data.Hidden;
    }
  }

  get alertService(): IAlertService {
    return container.get<IAlertService>(DiTokens.AlertService);
  }

  /** признак диалогового окна */
  dialog: boolean = false;

  /** контент диалогового окна */
  dialogContent: any = {};

  /**
 * Модель карты
 * @returns {CardModel} - Модель карты
 */
  get card(): CardModel {
    return this.contractsStore.contracts.getCard(this.contractId, this.cardId);
  }

  /**
 * Имя карты
 * @returns {string} - Имя карты
 */
  get getAlias(): string {
    return this.card?.Data.Alias;
  }

  /**
 * Информация о карте
 * @returns {Array<any>} - массив реквизитов
 */
  get details(): Array<any> {
    let details: Array<any> = [];
    if (this.contract && this.card) {
      details = [
        {
          disabled: this.setVisibilityLoading,
          name: i18n.t("main.contracts.details.show"),
          textVal: (isVisible: boolean) => { 
            return isVisible
            ? i18n.t("main.contracts.details.showContract")
            : i18n.t("main.contracts.details.hideContract")
          },
          isAction: true,
          model: !this.card.Data.Hidden,
          action: async () => {
            await this.setVisibility();
          }
        }
      ];
      const cardDetails: Array<any> = [
        {
          //Номер карты
          name: i18n.t("main.cards.details.cardNumber"),
          textVal: this.card.Data.Number
        },
        {
          //Тип карты
          name: i18n.t("main.cards.details.type"),
          textVal: this.card.CardPlasticTypeTitle
        },
        {
          //Номер договора
          name: i18n.t("main.cards.details.contractNum"),
          textVal: this.contract.Data.Number //ContractNumber
        },
        {
          //Срок действия
          name: i18n.t("main.cards.details.expDate"),
          textVal: this.card.Data.ExpDate
        },
        {
          //Состояние
          name: "Состояние",
          textVal: cardState(this.card.Data.CardState)
        },
        {
          //Валюта счета
          name: i18n.t("main.contracts.details.currency"),
          textVal: this.contract.Currency
        },
        {
          //Основной счет
          name: i18n.t("main.cards.details.mainAccount"),
          textVal: this.contract.Data.Alias
        },
        {
          //Номер основного счета
          name: i18n.t("main.cards.details.mainAccountNumber"),
          textVal: this.contract.Data.MainAccount
        },
        {
          //Доступный остаток на счете
          name: i18n.t("main.cards.details.acntDisposableLimit"),
          textVal: `${this.contract.DisposableLimit} ${this.contract.Currency}`
        }
      ];
      details.push(...cardDetails);
    }
    return details;
  }

  /**
 * Действия с картой
 * @returns {Array<any>} - массив действий
 */
  get actions(): Array<any> {
    const actions: Array<any> = [];
    if (this.contract && this.card) {
      if (this.card.IsActive) {
        if (
          this.contract.IsOpen &&
          this.contract.Data.CanDeposit &&
          this.configStore.apiData.OperationCodeOfToContract
        ) {
          /**Действие пополнения счета */
          actions.push({
            text: i18n.t("main.contracts.actions.fill"),
            iconClass: "fill-card-icon",
            action: () => {
              this.$router.push(
                `/operation/0/${this.configStore.apiData.OperationCodeOfToContract}?contractTo=${this.contract.Data.ReferenceId}`
              );
            }
          });
        }
        if (this.contract.IsOpen && this.contract.Data.CanWithdraw) {
          actions.push({
            /**Действие оплаты со счета */
            text: i18n.t("main.cards.actions.pay"),
            iconClass: "pay-icon",
            action: () => {
              this.$router.push(
                `/payments?contract=${this.contract.Data.ReferenceId}`
              );
            }
          });
        }

        /**Блокировка карты */
        actions.push({
          text: i18n.t("main.cards.actions.blockCard"),
          iconClass: "block-card-icon",
          action: () => {
            this.dialogContent = new Object({
              vueName: "CardBlockModal",
              params: {
                isBlock: true,
                contract: this.contract,
                card: this.card
              }
            });
            this.dialog = true;
          }
        });
        if (
          this.configStore.apiData.OperationCodeOfCardPinCode &&
          this.configStore.apiData.OperationCodeOfCardPinCode !== ""
        ) {
          // /**Сменить пин код */
          actions.push({
            text: i18n.t("main.cards.actions.changePin"),
            iconClass: "change-pin-icon",
            action: () => {
              this.dialogContent = new Object({
                vueName: "CardPinModal",
                params: { card: this.card }
              });
              this.dialog = true;
            }
          });
        }
        // /**Установить лимиты */
        // actions.push({
        //   text: i18n.t("main.cards.actions.setLimits"),
        //   iconClass: "set-limits-icon",
        //   action: () => {
        //     console.log("Установить лимиты");
        //   },
        // });
      } else if (this.card.IsBlocked) {
        /**Блокировка карты */
        actions.push({
          text: i18n.t("main.cards.actions.unblockCard"),
          iconClass: "block-card-icon",
          action: () => {
            this.dialogContent = new Object({
              vueName: "CardBlockModal",
              params: {
                isBlock: false,
                contract: this.contract,
                card: this.card
              }
            });
            this.dialog = true;
          }
        });
      }

      if (!this.card.IsIssued) {
        /**Действие запроса выписки по счету*/
        actions.push({
          text: i18n.t("main.cards.actions.statement"),
          iconClass: "statement-icon",
          action: () => {
            // this.statementMode = true;
            this.$router.push(
              `/statement/${this.contract.Data.Id}/${this.card.Data.Id}`
            );
          }
        });
      }
    }
    return actions;
  }

  get getTabtitle(): string {
    return this.$i18n.t("main.cards.details.about").toString();
  }

  /** Установка видимости карты */
  async setVisibility() {
    this.setVisibilityLoading = true;
    let model: ActionModel = new ActionModel();
    model.Data["contractId"] = this.contract.Data.Id;
    model.Data["cardId"] = this.card.Data.Id;
    model.Data["hidden"] = !this.card.Data.Hidden;
    model = (await this.contractsStore.setCardVisibility({ model: model, code: this.configStore.apiData.OperationCodeOfSetCardVisibility })) as ActionModel;
    if (model.IsError) this.isVisible = !this.isVisible; //в случае ошибки откатываем изменения
    this.setVisibilityLoading = false;
  }

  /**Изменение псевдонима счета */
  get editAlias(): () => Promise<void> {
    return async () => {
      const oldAlias = this.card.Data.Alias.trim();

      if (!this.isAliasValid) {
        this.alias = oldAlias;
        return;
      }

      if (this.alias == oldAlias) return;

      this.isAliasEdit = false;
      this.editAliasLoading = true;
      let model: ActionModel = new ActionModel();
      model.Data["contractId"] = this.contract.Data.Id;
      model.Data["cardId"] = this.card.Data.Id;
      model.Data["alias"] = this.alias;
      model = (await this.contractsStore.editCardAlias(model)) as ActionModel;
      if (model.IsError) this.alias = oldAlias; //в случае ошибки откатываем изменение

      this.editAliasLoading = false;
    };
  }
}
